import { forwardRef, type ReactNode } from 'react';
import * as RTabs from '@radix-ui/react-tabs';
import * as RCheckbox from '@radix-ui/react-checkbox';
import * as RSwitch from '@radix-ui/react-switch';
import { Check, Minus } from 'lucide-react';
import { cn } from './cn.js';

/**
 * Segmented control.
 *
 * The sliding indicator is a single absolutely-positioned element behind the
 * options rather than a background on the active button — that is what lets it
 * animate between positions instead of blinking, and it is the detail that
 * separates a segmented control from a row of buttons.
 */
export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  count?: number;
  title?: string;
}

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  size = 'md',
  className,
  'aria-label': ariaLabel,
}: {
  value: T;
  onChange: (v: T) => void;
  options: SegmentedOption<T>[];
  size?: 'sm' | 'md';
  className?: string;
  'aria-label'?: string;
}) {
  const index = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  );

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'relative inline-flex w-full min-w-0 items-center rounded-md border border-line bg-surface-sunken p-0.5',
        className,
      )}
    >
      <span
        aria-hidden
        className="absolute inset-y-0.5 rounded-sm bg-surface shadow-xs ring-1 ring-inset ring-line/70 transition-[left,width] duration-[var(--dur)] ease-out"
        style={{
          left: `calc(${(index / options.length) * 100}% + 2px)`,
          width: `calc(${100 / options.length}% - 4px)`,
        }}
      />
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            role="tab"
            type="button"
            aria-selected={active}
            title={o.title}
            onClick={() => onChange(o.value)}
            className={cn(
              'relative z-10 flex flex-1 items-center justify-center gap-1.5 truncate rounded-sm font-medium',
              'transition-colors duration-fast',
              size === 'sm' ? 'h-6 px-2 text-2xs' : 'h-7 px-2.5 text-xs',
              active ? 'text-ink' : 'text-ink-secondary hover:text-ink',
            )}
          >
            <span className="truncate">{o.label}</span>
            {o.count !== undefined && (
              <span
                className={cn(
                  'nums rounded-xs px-1 text-3xs tabular-nums',
                  active ? 'bg-brand-tint text-brand' : 'bg-surface-active text-ink-tertiary',
                )}
              >
                {o.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * A horizontal filter-chip row for when there are more options than a
 * segmented control can hold without shrinking each one to nothing.
 */
export function ChipGroup<T extends string>({
  value,
  onChange,
  options,
  className,
  'aria-label': ariaLabel,
}: {
  value: T;
  onChange: (v: T) => void;
  options: SegmentedOption<T>[];
  className?: string;
  'aria-label'?: string;
}) {
  return (
    <div role="tablist" aria-label={ariaLabel} className={cn('flex flex-wrap gap-1.5', className)}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            role="tab"
            type="button"
            aria-selected={active}
            title={o.title}
            onClick={() => onChange(o.value)}
            className={cn(
              'inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-medium',
              'transition-[background-color,box-shadow,color] duration-fast active:translate-y-px',
              active
                ? 'bg-brand text-ink-on-brand shadow-xs'
                : 'bg-surface text-ink-secondary ring-1 ring-inset ring-line hover:bg-surface-hover hover:text-ink',
            )}
          >
            {o.label}
            {o.count !== undefined && (
              <span
                className={cn(
                  'nums rounded-full px-1 text-3xs',
                  active ? 'bg-black/15 text-ink-on-brand' : 'bg-surface-sunken text-ink-tertiary',
                )}
              >
                {o.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tabs (for page-level sections)
// ---------------------------------------------------------------------------

export const Tabs = RTabs.Root;

export function TabsList({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <RTabs.List
      className={cn('relative flex items-center gap-4 border-b border-line', className)}
    >
      {children}
    </RTabs.List>
  );
}

export function TabsTrigger({
  value,
  children,
  count,
}: {
  value: string;
  children: ReactNode;
  count?: number;
}) {
  return (
    <RTabs.Trigger
      value={value}
      className={cn(
        'group relative -mb-px flex items-center gap-1.5 border-b-2 border-transparent pb-2 pt-1',
        'text-sm font-medium text-ink-secondary transition-colors duration-fast',
        'hover:text-ink',
        'data-[state=active]:border-brand data-[state=active]:text-ink',
      )}
    >
      {children}
      {count !== undefined && (
        <span className="nums rounded-xs bg-surface-sunken px-1 text-2xs text-ink-tertiary group-data-[state=active]:bg-brand-tint group-data-[state=active]:text-brand">
          {count}
        </span>
      )}
    </RTabs.Trigger>
  );
}

export const TabsContent = RTabs.Content;

// ---------------------------------------------------------------------------
// Checkbox and Switch
// ---------------------------------------------------------------------------

export const Checkbox = forwardRef<
  HTMLButtonElement,
  {
    checked: boolean | 'indeterminate';
    onCheckedChange: (c: boolean) => void;
    label?: ReactNode;
    hint?: ReactNode;
    disabled?: boolean;
    className?: string;
    id?: string;
  }
>(function Checkbox({ checked, onCheckedChange, label, hint, disabled, className, id }, ref) {
  const box = (
    <RCheckbox.Root
      ref={ref}
      id={id}
      checked={checked}
      onCheckedChange={(c) => onCheckedChange(c === true)}
      disabled={disabled}
      className={cn(
        'flex h-4 w-4 shrink-0 items-center justify-center rounded-xs border border-line-strong bg-surface',
        'transition-[background-color,border-color] duration-fast',
        'data-[state=checked]:border-brand data-[state=checked]:bg-brand',
        'data-[state=indeterminate]:border-brand data-[state=indeterminate]:bg-brand',
        'disabled:opacity-45',
        className,
      )}
    >
      <RCheckbox.Indicator className="text-ink-on-brand">
        {checked === 'indeterminate' ? (
          <Minus aria-hidden className="h-3 w-3" strokeWidth={3} />
        ) : (
          <Check aria-hidden className="h-3 w-3" strokeWidth={3} />
        )}
      </RCheckbox.Indicator>
    </RCheckbox.Root>
  );

  if (!label) return box;

  return (
    <label className="flex cursor-pointer items-start gap-2 text-sm">
      {box}
      <span className="min-w-0">
        <span className="block leading-snug text-ink">{label}</span>
        {hint && <span className="mt-0.5 block text-2xs text-ink-tertiary">{hint}</span>}
      </span>
    </label>
  );
});

export function Switch({
  checked,
  onCheckedChange,
  label,
  disabled,
  id,
}: {
  checked: boolean;
  onCheckedChange: (c: boolean) => void;
  label?: ReactNode;
  disabled?: boolean;
  id?: string;
}) {
  const control = (
    <RSwitch.Root
      id={id}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      className={cn(
        'relative h-4.5 w-8 shrink-0 rounded-full border border-line-strong bg-surface-sunken',
        'transition-colors duration-fast',
        'data-[state=checked]:border-brand data-[state=checked]:bg-brand',
        'disabled:opacity-45',
      )}
      style={{ height: '18px', width: '32px' }}
    >
      <RSwitch.Thumb
        className={cn(
          'block h-3.5 w-3.5 translate-x-0.5 rounded-full bg-surface shadow-xs',
          'transition-transform duration-fast',
          'data-[state=checked]:translate-x-[16px]',
        )}
      />
    </RSwitch.Root>
  );

  if (!label) return control;
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm text-ink">
      {control}
      {label}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Avatar
// ---------------------------------------------------------------------------

/**
 * Initials on a tinted disc, with the hue derived from the identity so the
 * same person is the same colour everywhere. Deterministic, and it never
 * invents a name — the caller passes whatever it legitimately has.
 */
const AVATAR_HUES = [
  'bg-brand-tint text-brand',
  'bg-info-tint text-info',
  'bg-accent-tint text-accent',
  'bg-ok-tint text-ok',
  'bg-danger-tint text-danger',
  'bg-warn-tint text-warn',
];

function hueFor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_HUES[h % AVATAR_HUES.length]!;
}

export function Avatar({
  name,
  email,
  size = 'md',
  className,
}: {
  name?: string | null;
  email?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const seed = (email ?? name ?? '?').toLowerCase();
  const initials = (() => {
    if (name && name.trim()) {
      const parts = name.trim().split(/\s+/);
      return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '')).toUpperCase();
    }
    return (email ?? '?').slice(0, 2).toUpperCase();
  })();

  const dim = {
    xs: 'h-4.5 w-4.5 text-3xs',
    sm: 'h-5 w-5 text-3xs',
    md: 'h-6 w-6 text-2xs',
    lg: 'h-8 w-8 text-xs',
  }[size];

  return (
    <span
      aria-hidden
      title={name ?? email ?? undefined}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-semibold ring-1 ring-inset ring-black/[0.04]',
        dim,
        hueFor(seed),
        className,
      )}
      style={size === 'xs' ? { height: 18, width: 18 } : undefined}
    >
      {initials}
    </span>
  );
}
